import { getAPI, postAPI } from "../../../../api/frameworksApi";

// Agent Health page (STOC): Application/Environment/Tool global filter, plus the
// per-tile endpoints that share this filter body shape.

const STOC_BASE_URL = (window.BE_URL || "") + "/stocApp/api/v1";
const AGENT_HEALTH_BASE_URL = `${STOC_BASE_URL}/agent-health`;


/** Shared Application/Environment/Tool filter; empty/omitted array (all tools
 * selected for `tools`) means "no filter" on that dimension. */
export interface AgentHealthGlobalFilter {
  applicationNames?: string[];
  environments?: string[];
  tools?: string[];
}

/** Source of truth for the Tool filter dropdown and the roster's
 * `toolCoverageTotal` denominator, so the two can't drift apart. */
export const fetchAgentHealthTools = async (): Promise<string[]> => {
  const data = await getAPI(`${AGENT_HEALTH_BASE_URL}/tools`);
  return Array.isArray(data) ? data : [];
};

export interface AgentHealthFreshnessSummary {
  total: number;
  needsAttention: number;
  healthyPercent: number;
  healthy: number;
  stale: number;
  failed: number;
  unknown: number;
}

export const fetchAgentHealthFreshnessSummary = async (
  filter: AgentHealthGlobalFilter,
): Promise<AgentHealthFreshnessSummary> =>
  postAPI(`${AGENT_HEALTH_BASE_URL}/integrations/freshness/summary`, filter);

export type AgentHealthFreshnessStatus = "Healthy" | "Stale" | "Failed" | "Unknown";

export interface AgentHealthFreshnessItem {
  tool: string;
  status: AgentHealthFreshnessStatus;
  /** Display-ready relative string (e.g. "5 min ago", "Never reported"), not a raw timestamp. */
  lastSuccessful: string;
  expected: string | null;
  /** Display-ready, same as `lastSuccessful` -- only set for "Failed" items. */
  latestAttempt: string | null;
  note: string | null;
  /** Minutes since last activity; a very large number for "Unknown" tools.
   * Drives the cards' default sort. */
  recencyMinutes: number;
}

/** Returns the full, unpaginated list -- search/sort/paging for the "View
 * all" modal are handled client-side. */
export const fetchAgentHealthFreshnessList = async (
  filter: AgentHealthGlobalFilter,
): Promise<AgentHealthFreshnessItem[]> =>
  postAPI(`${AGENT_HEALTH_BASE_URL}/integrations/freshness`, filter);

export interface AgentHealthFilterOptions {
  environments: string[];
  types: string[];
  agentStatuses: string[];
  /** Onboarding status options, e.g. ["Onboarded", "Not Onboarded"]. */
  assetStatuses?: string[];
}

/** Dropdown options for the roster's More Filters sheet. */
export const fetchAgentHealthFilterOptions = async (): Promise<AgentHealthFilterOptions> =>
  getAPI(`${AGENT_HEALTH_BASE_URL}/filter-options`);

export interface AgentHealthSummary {
  total: number;
  active: number;
  inactive: number;
  unreachable: number;
  neverReported: number;
}

export const fetchAgentHealthSummary = async (
  filter: AgentHealthGlobalFilter,
): Promise<AgentHealthSummary> => postAPI(`${AGENT_HEALTH_BASE_URL}/summary`, filter);

export type AgentHealthCoverageGroupBy = "application" | "organization" | "environment";

export interface AgentHealthCoverageRow {
  name: string;
  /** Count of assets with status Inactive, Unreachable, or Never Reported in this group. */
  unhealthy: number;
  /** Total asset count in this group, all statuses. */
  fleet: number;
}

/** For `groupBy: "organization"`, `name` is the division/organization name. */
export const fetchAgentHealthCoverage = async (
  filter: AgentHealthGlobalFilter & { groupBy: AgentHealthCoverageGroupBy },
): Promise<AgentHealthCoverageRow[]> => postAPI(`${AGENT_HEALTH_BASE_URL}/coverage`, filter);

export type AgentHealthMatrixStatus = "active" | "inactive" | "unreachable" | "never";

export interface AgentHealthStatusMatrixRow {
  application: string;
  environment: string;
  /** Worst status among the pairing's assets (never > unreachable > inactive > active). */
  status: AgentHealthMatrixStatus;
  /** Number of assets in this application x environment pairing. */
  assetCount?: number;
}

/** One entry per Application x Environment pairing that has at least one asset. */
export const fetchAgentHealthStatusMatrix = async (
  filter: AgentHealthGlobalFilter,
): Promise<AgentHealthStatusMatrixRow[]> =>
  postAPI(`${AGENT_HEALTH_BASE_URL}/status-matrix`, filter);

export type AgentHealthAssetStatus = "Active" | "Inactive" | "Unreachable" | "Never Reported";

export type AgentHealthAssetSortKey =
  | "assetName"
  | "type"
  | "ip"
  | "environment"
  | "division"
  | "department"
  | "application"
  | "toolCoverage";

/** Backs the roster table and every drilldown sheet; only one scope is ever
 * sent at a time -- `status` alone, `unhealthyOnly`+`groupBy`+`groupValue`,
 * or `matrixApplication`+`matrixEnvironment`+`matrixStatus`. */
export interface AgentHealthAssetsRequest extends AgentHealthGlobalFilter {
  /** Roster-only filters from the More Filters sheet. */
  types?: string[];
  agentStatuses?: string[];
  /** Matches assetName, type, ip, environment, division, department,
   * application, or any tool in toolCoverage. */
  search?: string;
  /** Status-tile drilldown; omit for "Total". */
  status?: AgentHealthAssetStatus;
  /** True when opened from a coverage bar. */
  unhealthyOnly?: boolean;
  groupBy?: AgentHealthCoverageGroupBy;
  groupValue?: string;
  /** Matrix-cell drilldown. */
  matrixApplication?: string;
  matrixEnvironment?: string;
  matrixStatus?: AgentHealthAssetStatus;
  page?: number;
  pageSize?: number;
  sortKey?: AgentHealthAssetSortKey;
  /** Tool name to sort by (dynamic tool columns); used instead of sortKey. */
  sortTool?: string;
  sortDir?: "asc" | "desc";
}

export interface AgentHealthAssetRow {
  id: string;
  assetName: string;
  type: string;
  ip: string;
  environment: string;
  division: string;
  department: string;
  application: string;
  status: AgentHealthAssetStatus;
  toolCoverageCount: number;
  toolCoverageTotal: number;
  applications?: string[];
  /** Per-tool agent status keyed by tool, e.g. { tenableIo: "Active" }; drives the dynamic tool columns. */
  tools?: Record<string, string>;
}

export interface AgentHealthAssetsResponse {
  /** Filtered count (drives pagination), not the whole fleet. */
  total: number;
  items: AgentHealthAssetRow[];
}

/** See {@link AgentHealthAssetsRequest} for how a caller scopes each view. */
export const fetchAgentHealthAssets = async (
  body: AgentHealthAssetsRequest,
): Promise<AgentHealthAssetsResponse> => postAPI(`${AGENT_HEALTH_BASE_URL}/assets`, body);

export interface AgentHealthAssetToolRow {
  tool: string;
  version: string | null;
  status: string;
  daysSinceReporting: string | null;
  lastHeartbeat: string | null;
}

/** Always returns 4 entries (one per tracked tool) regardless of install state. */
export const fetchAgentHealthAssetTools = async (
  assetId: string,
  tools: string[] = [],
): Promise<AgentHealthAssetToolRow[]> => {
  const query = tools.map((t) => `tools=${encodeURIComponent(t)}`).join("&");
  return getAPI(
    `${AGENT_HEALTH_BASE_URL}/assets/${encodeURIComponent(assetId)}/tools${query ? `?${query}` : ""}`,
  );
};
